/**
 * Gate.io WebSocket adapter — public futures + spot streams, no auth required.
 *
 * Futures: wss://fx-ws.gateio.ws/v4/ws/usdt  → futures.tickers
 * Spot:    wss://api.gateio.ws/ws/v4/         → spot.tickers
 */

import type { ExchangeWsAdapter } from "../live-pricing.types";

const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];

function normalizeFuturesSymbol(symbol: string): string {
  const upper = symbol.toUpperCase();
  if (upper.includes("_")) return upper;
  if (upper.endsWith("USDT")) return upper.replace("USDT", "_USDT");
  return `${upper}_USDT`;
}

interface GateFuturesTicker {
  contract?: string;
  mark_price?: string;
  index_price?: string;
  funding_rate?: string;
  last?: string;
  currency_pair?: string;
}

interface GateWsMsg {
  channel?: string;
  event?: string;
  result?: GateFuturesTicker[] | GateFuturesTicker;
}

function makeGateAdapter(
  wsUrl: string,
  channel: string,
  isSpot: boolean
): ExchangeWsAdapter {
  return {
    subscribe(symbol, onUpdate, onError) {
      const normalized = normalizeFuturesSymbol(symbol);
      let ws: WebSocket | null = null;
      let stopped = false;
      let attempt = 0;

      function connect() {
        if (stopped) return;
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          ws?.send(
            JSON.stringify({
              time: Math.floor(Date.now() / 1000),
              channel,
              event: "subscribe",
              payload: [normalized],
            })
          );
        };

        ws.onmessage = (e: MessageEvent<string>) => {
          try {
            const msg = JSON.parse(e.data) as GateWsMsg;
            if (msg.channel !== channel || msg.event !== "update") return;

            const results = Array.isArray(msg.result)
              ? msg.result
              : msg.result
                ? [msg.result]
                : [];

            for (const ticker of results) {
              const contractKey = isSpot
                ? ticker.currency_pair
                : ticker.contract;
              if (contractKey !== normalized) continue;

              const markPrice = Number.parseFloat(
                (isSpot ? ticker.last : ticker.mark_price) ?? "0"
              );
              if (markPrice <= 0) continue;

              const indexPrice = Number.parseFloat(
                ticker.index_price ?? String(markPrice)
              );
              const fundingRate = Number.parseFloat(
                ticker.funding_rate ?? "0"
              );

              attempt = 0;
              onUpdate({
                symbol: normalized,
                exchange: "gate",
                markPrice,
                indexPrice,
                fundingRate,
                nextFundingTime: 0,
                timestamp: Date.now(),
              });
            }
          } catch {
            // ignore parse errors
          }
        };

        ws.onerror = () => {
          // followed by close
        };

        ws.onclose = () => {
          if (stopped) return;
          const delay =
            RECONNECT_DELAYS_MS[
              Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
            ];
          attempt++;
          if (attempt > RECONNECT_DELAYS_MS.length) {
            onError?.(new Error(`Gate WS closed after ${attempt} attempts`));
            return;
          }
          setTimeout(connect, delay);
        };
      }

      connect();

      return () => {
        stopped = true;
        ws?.close();
        ws = null;
      };
    },
  };
}

export const gateFuturesWsAdapter: ExchangeWsAdapter = makeGateAdapter(
  "wss://fx-ws.gateio.ws/v4/ws/usdt",
  "futures.tickers",
  false
);

export const gateSpotWsAdapter: ExchangeWsAdapter = makeGateAdapter(
  "wss://api.gateio.ws/ws/v4/",
  "spot.tickers",
  true
);
